import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from '../../redux/root-reducer';
import { getUserAsync, postUserSso } from '../../utils/api';
import { endpoints } from '../../utils/endpoints';
import { fetchJSON } from '../../utils/fetch';
import { logError } from '../../utils/logging';
import {
  AuthenticatedUserResult,
  AuthenticationResponse,
  LoadUserRequest,
} from './authentication';

const get = endpoints();

export const checkEventRegistrationStatus = async (
  slug: string,
  email: string,
  token: string,
): Promise<{ registered: boolean; blocked: boolean }> => {
  try {
    const registrationStatusResponse: {
      registered: boolean;
      blocked: boolean;
    } = await fetchJSON(get(`REGISTRATION_STATUS`)(slug), {
      method: 'POST',
      body: {
        email: email,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return registrationStatusResponse;
  } catch (err) {
    logError(err as any);
    return {
      registered: false,
      blocked: false,
    };
  }
};

export const authenticateUserAsync = async (
  eventSlug: string,
  token: string | null = null,
): Promise<AuthenticationResponse> => {
  token = token || (await postUserSso(eventSlug))?.token;
  if (!token) return { token: null, user: null };

  const user = await getUserAsync(token);

  if (!user) return { token: null, user: null };

  return {
    token,
    user,
  };
};

// Unfortunately this returns CORS errors on localhost.
export const signOut = createAction('authentication/signOut');

export const loadUserAsync = createAsyncThunk<
  AuthenticatedUserResult,
  LoadUserRequest | undefined,
  { state: RootState; rejectValue: null }
>('authentication/loadUser', async (payload, { rejectWithValue, getState }) => {
  const state = getState();
  const eventSlug = state.eventDetails.event.url;

  const authenticationResult = await authenticateUserAsync(
    eventSlug,
    payload?.token,
  );

  if (authenticationResult.token === null) return rejectWithValue(null);

  const registrationStatus = await checkEventRegistrationStatus(
    eventSlug,
    authenticationResult.user.email,
    authenticationResult.token,
  );

  return {
    ...authenticationResult,
    ...registrationStatus,
    isLoggedIn: Boolean(
      authenticationResult?.token && authenticationResult?.user,
    ),
  };
});
